/**
 * Tab Style Tokens
 * Regressive sizes for visual hierarchy in nested tabs
 */

export const TABS = {
  // Wrapper for horizontal scroll on mobile
  scrollWrapper: "overflow-x-auto -mx-1 px-1",
  
  // ─────────────────────────────────────────────────────────────────────────
  // LEVEL 1 (Primary) - Main page navigation tabs
  // Height: h-11, Font: text-sm, Icons: h-4 w-4
  // NO margin-bottom - spacing handled by contentTopLevel on TabsContent
  // ─────────────────────────────────────────────────────────────────────────
  list: "inline-flex whitespace-nowrap h-11",
  trigger: "gap-1.5 px-4 py-2 text-sm",
  triggerWithBadge: "gap-2 px-4 py-2 text-sm",
  triggerResponsive: "gap-1.5 sm:gap-2 text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2",
  icon: "h-4 w-4 text-muted-foreground",
  iconActive: "h-4 w-4",
  
  // ─────────────────────────────────────────────────────────────────────────
  // LEVEL 2 (Secondary) - Nested tabs within a section
  // Height: h-9, Font: text-xs, Icons: h-3.5 w-3.5
  // NO margin-bottom - spacing handled by contentNested on TabsContent
  // ─────────────────────────────────────────────────────────────────────────
  listSecondary: "inline-flex whitespace-nowrap h-9",
  triggerSecondary: "gap-1 px-3 py-1.5 text-xs",
  triggerSecondaryWithBadge: "gap-1.5 px-3 py-1.5 text-xs",
  triggerSecondaryResponsive: "gap-1 sm:gap-1.5 text-xs px-2.5 sm:px-3 py-1 sm:py-1.5",
  iconSecondary: "h-3.5 w-3.5 text-muted-foreground",
  
  // ─────────────────────────────────────────────────────────────────────────
  // LEVEL 3 (Tertiary) - Deeply nested tabs (rare)
  // Height: h-8, Font: text-xs, Icons: h-3 w-3
  // NO margin-bottom - spacing handled by contentTertiary on TabsContent
  // ─────────────────────────────────────────────────────────────────────────
  listTertiary: "inline-flex whitespace-nowrap h-8",
  triggerTertiary: "gap-1 px-2.5 py-1 text-xs",
  iconTertiary: "h-3 w-3 text-muted-foreground",
  
  // TabsContent spacing by level - SINGLE SOURCE OF TRUTH for tab-to-content gap
  // SPACING RULE: Consistent 16px gap after primary/secondary tabs, 12px for tertiary
  // This ensures uniform spacing whether next element is nested tabs or content
  contentTopLevel: "mt-4",   // 16px - after primary tabs (normalized)
  contentNested: "mt-4",     // 16px - after secondary tabs  
  contentTertiary: "mt-3",   // 12px - after tertiary tabs
  
  // ─────────────────────────────────────────────────────────────────────────
  // SPECIAL VARIANTS - Grid-based and compact tabs
  // ─────────────────────────────────────────────────────────────────────────
  
  // Grid-based tabs (for Auth page 2-column, Experts view toggles)
  listGrid2: "grid w-full grid-cols-2",
  listGrid3: "grid w-full grid-cols-3",
  triggerGrid: "gap-1.5",
  
  // Compact inline tabs (for filters, date range selectors)
  listCompact: "inline-flex",
  triggerCompact: "text-xs sm:text-sm px-2 py-1",
} as const;

export type TabToken = keyof typeof TABS;
